import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { TextArea } from 'components/atoms';
import {
  AIVoiceSection,
  ToneOfVoiceDropdown,
  BackgroundMusicSection,
  AudioScriptPlaybackCard,
} from 'components/molecules';
import { InputField } from 'components/atoms/input-field';
import { Form } from 'react-final-form';
import {
  businessNameValidator,
  createMainMessageValidatorWithBackendCheck,
} from 'utils/gen-ai-audio-tool/validation';
import { useTranslation } from 'next-i18next';
import { useMixedVoiceOverMutation } from 'hooks/api/v3/useMixedVoiceOverMutation';
import {
  VoiceoverTone,
  GenerateMixedVoiceoverResponse,
  TextWithTimestamp,
  GenerateAdError,
  GenerateAdErrorNameEnum,
  ResponseError,
} from '@spotify-internal/ads-api-typescript-client-development';
import { VOICEOVER_TONE_MAP } from 'components/molecules/tone-of-voice-dropdown/constants';
import { VOICE_IDS } from 'components/molecules/ai-voice-section/constants';

import * as Styled from './GenAITool.styled';

export type GenAIToolFormValues = {
  businessName: string;
  mainMessage: string;
  toneOfVoice: string;
  aiVoice: string;
  backgroundMusic: string;
};

export enum GenerateButtonState {
  // eslint-disable-next-line no-unused-vars
  Idle = 'idle',
  // eslint-disable-next-line no-unused-vars
  Generating = 'generating',
  // eslint-disable-next-line no-unused-vars
  Completed = 'completed',
}

export type AudioTrackInfo = {
  id: string;
  audioElement: HTMLAudioElement | null;
};

// Helper function to convert TextWithTimestamp array to string
const formatTranscript = (
  transcript: string | TextWithTimestamp[] | undefined,
): string => {
  if (!transcript) return '';
  if (typeof transcript === 'string') return transcript;
  return transcript.map(item => item.text).join(' ');
};

export const GenAITool = () => {
  const [buttonState, setButtonState] = useState<GenerateButtonState>(
    GenerateButtonState.Idle,
  );
  const [generatedAt, setGeneratedAt] = useState<Date | undefined>(undefined);
  const [
    audioResponse,
    setAudioResponse,
  ] = useState<GenerateMixedVoiceoverResponse | null>(null);
  const [generatedAdError, setGeneratedAdError] = useState<string | null>(null);

  const { t } = useTranslation();
  const ERROR_MESSAGES = useMemo(
    () =>
      ({
        GENERAL_ERROR: t('Something went wrong. Please try again.'),
        AD_BLOCKER_ERROR: t(
          'Something went wrong. Turn off ad block and try again',
        ),
      } as const),
    [t],
  );
  const [errorBanner, setErrorBanner] = useState<
    keyof typeof ERROR_MESSAGES | null
  >(null);
  const [currentAudioTrack, setCurrentAudioTrack] = useState<AudioTrackInfo>({
    id: '',
    audioElement: null,
  });
  const formRef = useRef<any>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const fieldBusinessNameRef = useRef<HTMLDivElement>(null);
  const fieldMainMessageRef = useRef<HTMLDivElement>(null);
  const errorBannerRef = useRef<HTMLDivElement>(null);

  // Map of field names to their refs, in priority order
  const sectionsToScrollTo = useMemo(
    () =>
      ({
        errorBanner: {
          ref: errorBannerRef,
        },
        fieldBusinessName: {
          name: 'businessName',
          ref: fieldBusinessNameRef,
        },
        fieldMainMessage: {
          name: 'mainMessage',
          ref: fieldMainMessageRef,
        },
        preview: {
          ref: previewRef,
        },
      } as const),
    [],
  );

  const scrollToSection = useCallback(
    (section: keyof typeof sectionsToScrollTo) => {
      requestAnimationFrame(() => {
        sectionsToScrollTo[section].ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    },
    [sectionsToScrollTo],
  );

  const scrollToFirstFieldError = useCallback(
    ({ errors }: { errors?: Record<string, string> }) => {
      const firstErrorKey = Object.keys(errors ?? {}).find(
        errorKey => errors?.[errorKey],
      );

      if (!firstErrorKey) return;

      const sectionKeys = Object.keys(
        sectionsToScrollTo,
      ) as (keyof typeof sectionsToScrollTo)[];

      const section = sectionKeys.find(key => {
        const currentSection = sectionsToScrollTo[key];
        return (
          'name' in currentSection && currentSection.name === firstErrorKey
        );
      });

      if (section) {
        scrollToSection(section);
      }
    },
    [sectionsToScrollTo, scrollToSection],
  );

  useEffect(
    function scrollToErrorBanner() {
      if (errorBanner) {
        scrollToSection('errorBanner');
      }
    },
    [errorBanner],
  );

  // Effect to trigger validation when backend error changes
  useEffect(() => {
    if (generatedAdError && formRef.current) {
      const formState = formRef.current.getState();
      if (formState && formState.values && formState.values.mainMessage) {
        const currentValue = formState.values.mainMessage;
        formRef.current.batch(() => {
          formRef.current.change('mainMessage', `${currentValue} `); // change to empty string to trigger validation
          formRef.current.change('mainMessage', currentValue);
          scrollToSection('fieldMainMessage');
        });
      }
    }
  }, [generatedAdError]);

  const {
    mutate: generateMixedVoiceOver,
    isPending: isGeneratingAudio,
  } = useMixedVoiceOverMutation();

  const getButtonText = () => {
    switch (buttonState) {
      case GenerateButtonState.Generating:
        return 'Generating...';
      case GenerateButtonState.Completed:
        return 'Regenerate';
      default:
        return 'Generate';
    }
  };

  // Helper function to format error messages from backend
  const formatErrorMessage = (errors: GenerateAdError[]): string => {
    if (!errors || errors.length === 0) return '';

    // Format error message based on error type
    const error = errors[0];

    switch (error.name) {
      case GenerateAdErrorNameEnum.ScriptGenerationFailed:
        return "Couldn't generate scripts. Include more details about your message and try again.";
      case GenerateAdErrorNameEnum.ScriptOffensive:
      case GenerateAdErrorNameEnum.ScriptGuardrailTopic:
        return 'Audio generation for this topic is restricted in this demo. <a href="https://adshelp.spotify.com/s/article/advertising-policies-US?language=en_US" target="_blank" rel="noopener noreferrer" style="color: #e91429; text-decoration: underline;">Learn more about category restrictions</a>';
      default:
        return '';
    }
  };

  const handleGenerate = async (values: GenAIToolFormValues) => {
    setButtonState(GenerateButtonState.Generating);
    setGeneratedAdError(null); // Clear previous errors
    setErrorBanner(null); // Clear previous error banner
    scrollToSection('preview');

    if (
      currentAudioTrack.id === 'script' &&
      currentAudioTrack.audioElement &&
      !currentAudioTrack.audioElement.paused
    ) {
      currentAudioTrack.audioElement.currentTime = 0;
      currentAudioTrack.audioElement.pause();
    }

    generateMixedVoiceOver(
      {
        brandName: values.businessName,
        talkingPoints: values.mainMessage,
        aiVoiceoverTone:
          VOICEOVER_TONE_MAP[values.toneOfVoice] ||
          VoiceoverTone.Conversational,
        voiceProfileId: values.aiVoice,
        backgroundId: values.backgroundMusic,
      },
      {
        onSuccess: data => {
          // Check if response contains errors
          if (data.errors && data.errors.length > 0) {
            const generatedAdErrorMessages = formatErrorMessage(data.errors);
            if (generatedAdErrorMessages) {
              setGeneratedAdError(generatedAdErrorMessages); // for Restricted content
            } else {
              setErrorBanner('GENERAL_ERROR');
            }
            setButtonState(GenerateButtonState.Idle);
          } else {
            setAudioResponse(data);
            setButtonState(GenerateButtonState.Completed);
            setGeneratedAt(new Date());
            scrollToSection('preview');
          }
        },
        onError: error => {
          setButtonState(GenerateButtonState.Idle);
          const responseError = error as ResponseError;
          if (
            responseError?.response?.status === 0 ||
            responseError?.response?.status === 500
          ) {
            setErrorBanner('AD_BLOCKER_ERROR');
          } else {
            setErrorBanner('GENERAL_ERROR');
          }
        },
      },
    );
  };

  const initialValues: GenAIToolFormValues = {
    businessName: '',
    mainMessage: '',
    toneOfVoice: 'conversational_friendly',
    aiVoice: VOICE_IDS.ADDISON,
    backgroundMusic: 'b420ff18-1cf6-447e-9f0c-9eaf46279adb',
  };

  // Get audio URL from response
  const audioUrl = audioResponse?.mixUrl || '';

  // Convert transcript to string format for AudioScriptPlaybackCard
  const script = formatTranscript(audioResponse?.transcript);

  // Create validator that includes backend errors when needed
  const mainMessageValidatorWithBackendError = createMainMessageValidatorWithBackendCheck(
    generatedAdError,
    () => setGeneratedAdError(null),
  );

  const handlePlayAudio = ({
    id,
    audioElement,
    playFromStart = false,
  }: Pick<AudioTrackInfo, 'id' | 'audioElement'> & {
    playFromStart?: boolean;
  }) => {
    // Stop any currently playing audio
    if (currentAudioTrack?.id !== id && currentAudioTrack?.audioElement) {
      currentAudioTrack.audioElement.pause();
      currentAudioTrack.audioElement.currentTime = 0;
    }
    // Start playing the new audio
    if (playFromStart && audioElement) {
      // eslint-disable-next-line no-param-reassign
      audioElement.currentTime = 0;
    }
    audioElement?.play();
    setCurrentAudioTrack({ id, audioElement });
  };

  const handlePauseAudio = () => {
    if (currentAudioTrack?.audioElement) {
      currentAudioTrack.audioElement.pause();
      setCurrentAudioTrack(currentAudioTrackState => ({
        ...currentAudioTrackState,
      }));
    }
  };

  const handleAudioEnded = () => {
    setCurrentAudioTrack({ id: '', audioElement: null });
  };

  return (
    <Styled.GridWrapper>
      {errorBanner && (
        <Styled.ErrorBanner ref={errorBannerRef} data-testid="error-banner">
          <Styled.ErrorIcon name="form-error" />
          <Styled.ErrorMessage>
            {ERROR_MESSAGES[errorBanner || 'GENERAL_ERROR']}
          </Styled.ErrorMessage>
        </Styled.ErrorBanner>
      )}
      <Form<GenAIToolFormValues>
        onSubmit={handleGenerate}
        initialValues={initialValues}
        render={({ handleSubmit, form, errors }) => {
          // Store form reference
          formRef.current = form;
          return (
            <Styled.ContentGrid data-testid="content-grid">
              <form
                onSubmit={handleSubmit}
                id="gen-ai-audio-tool-form"
                data-testid="gen-ai-audio-tool-form"
                style={{ display: 'contents' }}
              >
                <Styled.ContentGridFormArea data-testid="content-grid-area-form">
                  <div ref={fieldBusinessNameRef}>
                    <Styled.MessageField>
                      <InputField
                        name={sectionsToScrollTo.fieldBusinessName.name}
                        fieldName={sectionsToScrollTo.fieldBusinessName.name}
                        placeholder="Business name"
                        validator={businessNameValidator(t)}
                      />
                    </Styled.MessageField>
                  </div>
                  <div ref={fieldMainMessageRef}>
                    <Styled.MessageField>
                      <TextArea
                        name={sectionsToScrollTo.fieldMainMessage.name}
                        fieldName={sectionsToScrollTo.fieldMainMessage.name}
                        placeholder="Main message"
                        validator={mainMessageValidatorWithBackendError(t)}
                        rows={4}
                      />
                      <Styled.MessageFieldLabel>
                        Describe the core message of your audio in 1-2
                        sentences.
                      </Styled.MessageFieldLabel>
                    </Styled.MessageField>
                  </div>
                  <ToneOfVoiceDropdown fieldName="toneOfVoice" />
                </Styled.ContentGridFormArea>
                <Styled.ContentGridCarouselArea data-testid="content-grid-area-carousel">
                  <AIVoiceSection
                    fieldName="aiVoice"
                    handlePlayAudio={handlePlayAudio}
                    handlePauseAudio={handlePauseAudio}
                    handleAudioEnded={handleAudioEnded}
                    currentAudioId={currentAudioTrack.id}
                  />
                  <BackgroundMusicSection
                    fieldName="backgroundMusic"
                    handlePlayAudio={handlePlayAudio}
                    handlePauseAudio={handlePauseAudio}
                    handleAudioEnded={handleAudioEnded}
                    currentAudioId={currentAudioTrack.id}
                  />
                </Styled.ContentGridCarouselArea>
              </form>
              <Styled.ContentGridSubmitArea data-testid="content-grid-area-submit">
                <Styled.GenerateButton
                  type="submit"
                  form="gen-ai-audio-tool-form"
                  data-testid={
                    buttonState === GenerateButtonState.Completed
                      ? 'gen-ai-audio-tool-form-regenerate-button'
                      : 'gen-ai-audio-tool-form-generate-button'
                  }
                  disabled={
                    buttonState === GenerateButtonState.Generating ||
                    isGeneratingAudio
                  }
                  onClick={() => scrollToFirstFieldError({ errors })}
                >
                  {getButtonText()}
                </Styled.GenerateButton>
              </Styled.ContentGridSubmitArea>
              <Styled.ContentGridPreviewArea
                data-testid="content-grid-area-preview"
                ref={previewRef}
              >
                <AudioScriptPlaybackCard
                  audioUrl={audioUrl}
                  script={script}
                  isLoading={isGeneratingAudio}
                  generatedAt={generatedAt}
                  handlePlayAudio={handlePlayAudio}
                  handlePauseAudio={handlePauseAudio}
                  isPlaying={
                    currentAudioTrack.id === 'script' &&
                    !currentAudioTrack.audioElement?.paused
                  }
                  handleAudioEnded={handleAudioEnded}
                />
              </Styled.ContentGridPreviewArea>
              <Styled.LegalDisclaimer>
                By clicking &quot;Generate&quot; you acknowledge and agree that
                this tool is subject to the Spotify Terms of Use and intended
                for demo purposes only. Generated audio is not intended for
                commercial use and will not be saved in your Spotify Ads Manager
                account.
              </Styled.LegalDisclaimer>
            </Styled.ContentGrid>
          );
        }}
      />
    </Styled.GridWrapper>
  );
};
